import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["apiKey"]

  static values = {
    redirectUrl: String
  }

  initOauth() {
    const oauthData = this.#getOauthData()

    if (!oauthData) {
      console.error("[OAuth]: this authentication scheme doesn't have any OAuth data")
      return
    }

    if (oauthData.grantType === "implicit") {
      // Implicit grant type
      // https://datatracker.ietf.org/doc/html/rfc6749#section-4.2

      // 1. Compute a random state to be verified during the oauth response
      this.authorizationState = `bump.sh-${Math.random().toString(32).substring(2)}${Math.random().toString(32).substring(2)}`

      // 2. Build the target auth URL with relevant query params
      // (depends on the grant type)
      this.authorizationUrl = new URL(oauthData.authorizationUrl)
      const authorizationQueryParams = new URLSearchParams(this.authorizationUrl.search)
      authorizationQueryParams.set("response_type", "token")
      authorizationQueryParams.set("client_id", oauthData.clientId)
      authorizationQueryParams.set("redirect_uri", this.redirectUrlValue)
      authorizationQueryParams.set("state", this.authorizationState)
      this.authorizationUrl.search = authorizationQueryParams.toString()

      // 3. Launch authentication by visiting the target auth URL
      setTimeout(this.#authenticateClient.bind(this), 10)
    } else {
      console.error(`[OAuth]: Unsupported ${oauthData.grantType} grant type. Please contact hello@bump.sh`)
    }
  }

  #getOauthData() {
    const authenticationScheme = this.#selectedAuthenticationScheme()

    return JSON.parse(authenticationScheme.dataset.oauth)
  }

  #selectedAuthenticationScheme() {
    return this.element.querySelector("[data-selected-scheme=true]")
  }

  #authenticateClient() {
    this.authorizationWindow = window.open(this.authorizationUrl.toString())
    window.addEventListener("message", this.#receiveAuthenticationResponse.bind(this), {once: true})
  }

  #receiveAuthenticationResponse(event) {
    // Close authorization window as we don't need it anymore
    if (this.authorizationWindow) {
      this.authorizationWindow.close()
    }

    // Ignore any messages received from windows which origin is
    // different from the current location origin
    if (event.origin !== window.location.origin) {
      console.error(`[OAuth]: received an event from a incorrect origin (${event.origin}). It has been ignored`)
      return
    }

    if (!event || !event.data) {
      console.error("[OAuth]: empty message event from authorization window")
      return
    }

    if (event.data.error) {
      console.error(`[OAuth]: an error was returned from the authorization server: ${event.data.error}`)
      return
    }

    const {state, accessToken} = event.data
    if (state !== this.authorizationState) {
      console.error("[OAuth]: the authorization response has an incorrect 'state' value! It has been ignored")
      return
    }

    // SUCCESS Authentication
    // 1. Enter the access token in our ApiKey field
    this.apiKeyTarget.value = accessToken
    // 2. Simulate user input for other events to bubble up (store key in
    // local storage, update UI, etc..)
    this.apiKeyTarget.dispatchEvent(new Event("input", { bubbles: true }))
    // 3. Finally, dispatch an event for other UI elements (e.g. close
    // the auth dialog)
    this.dispatch("success")
  }
}
